/**
 * Flush reject-guard for StudyGroup reconcile pushes.
 *
 * `flushSgLocalDirty` re-sends every locally-dirty SG row on each
 * reconnect. Unlike an edit-time mutation it has no pre-image to revert,
 * so a push the server *rejects* (e.g. a non-owner editing an owner-only
 * deck's properties, or a stale-LWW card) would otherwise be re-emitted
 * verbatim on every reconnect — the `sg-sync-error` storm.
 *
 * This guard remembers the exact `(scope, key, version)` the server
 * rejected so the flush stops re-pushing THAT version. A genuinely new
 * edit produces a new version key (newer timestamp) and is retried
 * normally — we only suppress the precise version the server refused.
 *
 * Lifecycle:
 *   1. flush emits a push with a fresh `mutationId` → `recordFlushMutation`.
 *   2. server `op-ack`     → `forgetFlushMutation` (drop the bookkeeping).
 *      server `op-rejected`→ `noteFlushMutationRejected` (remember version).
 *   3. next flush → `isFlushVersionRejected` skips the refused version.
 *
 * In-memory + per-session by design: a fresh session re-derives dirtiness
 * from the store and a reconnect re-attempts, which is the desired
 * "try once more after a real reload" behavior.
 */

const rejectedVersions = new Set<string>();
const mutationToVersion = new Map<string, string>();

/** Stable key for one row at one version on one group's flush channel. */
export function flushVersionKey(
  groupId: string,
  wireType: string,
  rowKey: string,
  version: string,
): string {
  return `${groupId}\x01${wireType}\x01${rowKey}\x01${version}`;
}

/** Called when the flush emits a push, before it goes on the wire. */
export function recordFlushMutation(mutationId: string, versionKey: string): void {
  mutationToVersion.set(mutationId, versionKey);
}

/** Called from the universal `op-rejected` handler. */
export function noteFlushMutationRejected(mutationId: string): void {
  const versionKey = mutationToVersion.get(mutationId);
  if (versionKey === undefined) return;
  rejectedVersions.add(versionKey);
  mutationToVersion.delete(mutationId);
}

/** Called from the universal `op-ack` handler (success → drop bookkeeping). */
export function forgetFlushMutation(mutationId: string): void {
  mutationToVersion.delete(mutationId);
}

/**
 * Read the version key of a still-pending flush mutation WITHOUT dropping
 * it. A scope's `onPushAck` peeks this on `op-ack` to recover the
 * `(deckId, version)` it just confirmed — e.g. to advance the SG per-deck
 * shared-card clock — before the universal handler forgets the mutation.
 */
export function peekFlushMutationVersion(mutationId: string): string | undefined {
  return mutationToVersion.get(mutationId);
}

/** Flush consults this to skip a version the server already refused. */
export function isFlushVersionRejected(versionKey: string): boolean {
  return rejectedVersions.has(versionKey);
}

/** Test-only: clear all guard state. */
export function _resetFlushRejectGuard(): void {
  rejectedVersions.clear();
  mutationToVersion.clear();
}
